import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import DateRangePicker from "react-bootstrap-daterangepicker";
import "bootstrap-daterangepicker/daterangepicker.css";
import dayjs from "dayjs";
import { AuthContext } from "../../../../context/authcontext";
import { useFilterOptions } from "../../../../hooks/useFilterOptions";
import { CurrencySelect } from "../../../../components/currency";
import { useStoredCurrencyCode } from "../../../../components/currencyFormat";
import { PaginatedAsyncSelect } from "../../../../components/UniversalForm/InfiniteScrollSelect";
import { SelectOption } from "../../../../components/UniversalForm/types";
import { fetchContactPage, DashboardApiFilters } from "../dashboardApi";
import "./dashboardFilters.css";

export interface DashboardFilterState {
  companyId: number | null;
  companyName: string;
  contactId: number | null;
  contactName: string;
  teamId: number | null;
  teamName: string;
  userId: number | null;
  userName: string;
  currencyCode: string | null;
  status: string | null;
  startDate: string | null;
  endDate: string | null;
}

export const emptyDashboardFilters: DashboardFilterState = {
  companyId: null,
  companyName: "",
  contactId: null,
  contactName: "",
  teamId: null,
  teamName: "",
  userId: null,
  userName: "",
  currencyCode: null,
  status: null,
  startDate: null,
  endDate: null,
};

export const DASHBOARD_STATUS_OPTIONS = [
  { value: "open", label: "Open" },
  { value: "won", label: "Won" },
  { value: "lost", label: "Lost" },
];

const statusLabel = (status: string | null) =>
  DASHBOARD_STATUS_OPTIONS.find((option) => option.value === status)?.label ?? status ?? "";

export const resolveTenantCurrency = (auth: any, tenantId: string | null): string | null => {
  const tenants: any[] = Array.isArray(auth?.tenants) ? auth.tenants : [];
  const tenant = tenants.find((item) => String(item?.tenant_id) === String(tenantId)) || tenants[0];
  const lifecycleTypes: any[] = Array.isArray(tenant?.lifecycle_types) ? tenant.lifecycle_types : [];
  return lifecycleTypes.find((item) => item?.currency_code)?.currency_code || null;
};

export const useDefaultDashboardFilters = () => {
  const { auth, selectedTenantId } = useContext(AuthContext);
  const storedCurrency = useStoredCurrencyCode(selectedTenantId);
  const currencyCode = storedCurrency || resolveTenantCurrency(auth, selectedTenantId);
  return useMemo<DashboardFilterState>(() => ({ ...emptyDashboardFilters, currencyCode }), [currencyCode]);
};

export const useDashboardFilters = () => {
  const defaults = useDefaultDashboardFilters();
  const { selectedTenantId } = useContext(AuthContext);
  const [filters, setFilters] = useState<DashboardFilterState>(defaults);

  useEffect(() => {
    setFilters(defaults);
  }, [selectedTenantId, defaults]);

  return { filters, setFilters, defaults };
};

const API_DATE_FORMAT = "YYYY-MM-DD";
const DISPLAY_DATE_FORMAT = "DD MMM YYYY";

export const formatDateRange = (startDate: string | null, endDate: string | null) =>
  startDate && endDate
    ? `${dayjs(startDate).format(DISPLAY_DATE_FORMAT)} - ${dayjs(endDate).format(DISPLAY_DATE_FORMAT)}`
    : "";

export const countActiveFilters = (
  filters: DashboardFilterState,
  defaults: DashboardFilterState = emptyDashboardFilters
) =>
  [filters.companyId, filters.contactId, filters.teamId, filters.userId, filters.status, filters.startDate].filter(
    (value) => value !== null
  ).length + (filters.currencyCode !== defaults.currencyCode ? 1 : 0);

export const toApiFilters = (filters: DashboardFilterState): DashboardApiFilters => ({
  companyId: filters.companyId,
  contactId: filters.contactId,
  assignedTeamId: filters.teamId,
  assignedUserId: filters.userId,
  currencyCode: filters.currencyCode,
  status: filters.status,
  startDate: filters.startDate,
  endDate: filters.endDate,
});

interface FilterChip {
  key: string;
  icon: string;
  label: string;
  value: string;
  isDefault: boolean;
  next: DashboardFilterState | null;
}

export const DEFAULT_DATE_RANGE_LABEL = "Last 1 Month";

const defaultRangeStart = () => dayjs().subtract(1, "month");

export const defaultDateRange = () => ({
  startDate: defaultRangeStart().format(API_DATE_FORMAT),
  endDate: dayjs().format(API_DATE_FORMAT),
});

const formatCompactRange = (startDate: string, endDate: string) => {
  const start = dayjs(startDate);
  const end = dayjs(endDate);
  return start.year() === end.year()
    ? `${start.format("DD MMM")} – ${end.format(DISPLAY_DATE_FORMAT)}`
    : `${start.format(DISPLAY_DATE_FORMAT)} – ${end.format(DISPLAY_DATE_FORMAT)}`;
};

export const hasFilterChips = (
  filters: DashboardFilterState,
  defaults: DashboardFilterState,
  showDefaultDateRange = false
) => Boolean(filters.currencyCode) || countActiveFilters(filters, defaults) > 0 || showDefaultDateRange;

const getFilterChips = (
  filters: DashboardFilterState,
  defaults: DashboardFilterState,
  showDefaultDateRange = false
): FilterChip[] => {
  const chips: FilterChip[] = [];
  if (filters.currencyCode) {
    const isDefault = filters.currencyCode === defaults.currencyCode;
    chips.push({
      key: "currency",
      icon: "ti ti-coin",
      label: "Currency",
      value: filters.currencyCode,
      isDefault,
      next: isDefault ? null : { ...filters, currencyCode: defaults.currencyCode },
    });
  }
  if (filters.startDate !== null && filters.endDate !== null) {
    chips.push({
      key: "date",
      icon: "ti ti-calendar",
      label: "Date",
      value: formatCompactRange(filters.startDate, filters.endDate),
      isDefault: false,
      next: { ...filters, startDate: null, endDate: null },
    });
  } else if (showDefaultDateRange) {
    const range = defaultDateRange();
    chips.push({
      key: "default-date",
      icon: "ti ti-calendar",
      label: DEFAULT_DATE_RANGE_LABEL,
      value: formatCompactRange(range.startDate, range.endDate),
      isDefault: true,
      next: null,
    });
  }
  if (filters.status !== null) {
    chips.push({
      key: "status",
      icon: "ti ti-progress-check",
      label: "Status",
      value: statusLabel(filters.status),
      isDefault: false,
      next: { ...filters, status: null },
    });
  }
  if (filters.companyId !== null) {
    chips.push({
      key: "company",
      icon: "ti ti-building",
      label: "Company",
      value: filters.companyName || String(filters.companyId),
      isDefault: false,
      next: { ...filters, companyId: null, companyName: "", contactId: null, contactName: "" },
    });
  }
  if (filters.contactId !== null) {
    chips.push({
      key: "contact",
      icon: "ti ti-address-book",
      label: "Contact",
      value: filters.contactName || String(filters.contactId),
      isDefault: false,
      next: { ...filters, contactId: null, contactName: "" },
    });
  }
  if (filters.teamId !== null) {
    chips.push({
      key: "team",
      icon: "ti ti-users-group",
      label: "Team",
      value: filters.teamName || String(filters.teamId),
      isDefault: false,
      next: { ...filters, teamId: null, teamName: "" },
    });
  }
  if (filters.userId !== null) {
    chips.push({
      key: "user",
      icon: "ti ti-user",
      label: "User",
      value: filters.userName || String(filters.userId),
      isDefault: false,
      next: { ...filters, userId: null, userName: "" },
    });
  }
  return chips;
};

export const describeFilters = (filters: DashboardFilterState, showDefaultDateRange = false) => {
  const labels = getFilterChips(filters, filters, showDefaultDateRange).map((chip) => `${chip.label}: ${chip.value}`);
  return labels.length ? `Filters: ${labels.join("  |  ")}` : "Filters: None (all data)";
};

export const ActiveFilterChips = ({
  filters,
  onChange,
  disabled = false,
  showDefaultDateRange = false,
}: {
  filters: DashboardFilterState;
  onChange: (filters: DashboardFilterState) => void;
  disabled?: boolean;
  showDefaultDateRange?: boolean;
}) => {
  const defaults = useDefaultDashboardFilters();
  const chips = getFilterChips(filters, defaults, showDefaultDateRange);
  if (!chips.length) return null;
  return (
    <div className="db-filter-summary d-flex align-items-center flex-wrap gap-2 mb-4">
      {chips.map((chip) => {
        const next = chip.next;
        return (
          <span
            key={chip.key}
            className={`db-filter-chip ${chip.isDefault ? "db-filter-chip-default" : "db-filter-chip-active"}`}
            title={chip.isDefault ? "Default filter" : undefined}
          >
            <i className={`${chip.icon} db-filter-chip-icon`} />
            <span className="db-filter-chip-label">{chip.label}</span>
            <span className="db-filter-chip-value">{chip.value}</span>
            {next && (
              <button
                type="button"
                aria-label={`Remove ${chip.label} filter`}
                onClick={() => onChange(next)}
                disabled={disabled}
              >
                <i className="ti ti-x" />
              </button>
            )}
          </span>
        );
      })}
      {countActiveFilters(filters, defaults) > 0 && (
        <button
          type="button"
          className="btn btn-link btn-sm p-0 db-filter-clear"
          onClick={() => onChange(defaults)}
          disabled={disabled}
        >
          Clear all
        </button>
      )}
    </div>
  );
};

const quarterStart = (date: dayjs.Dayjs) => date.month(Math.floor(date.month() / 3) * 3).startOf("month");

const dateRangeSettings = () => ({
  autoUpdateInput: false,
  opens: "left",
  startDate: defaultRangeStart().toDate(),
  endDate: dayjs().toDate(),
  locale: { cancelLabel: "Clear" },
  ranges: {
    Today: [dayjs().toDate(), dayjs().toDate()],
    "Last 7 Days": [dayjs().subtract(6, "day").toDate(), dayjs().toDate()],
    "Last 30 Days": [dayjs().subtract(29, "day").toDate(), dayjs().toDate()],
    [DEFAULT_DATE_RANGE_LABEL]: [defaultRangeStart().toDate(), dayjs().toDate()],
    "This Month": [dayjs().startOf("month").toDate(), dayjs().endOf("month").toDate()],
    "Last Month": [
      dayjs().subtract(1, "month").startOf("month").toDate(),
      dayjs().subtract(1, "month").endOf("month").toDate(),
    ],
    "This Quarter": [quarterStart(dayjs()).toDate(), dayjs().toDate()],
    "Last Quarter": [
      quarterStart(dayjs()).subtract(3, "month").toDate(),
      quarterStart(dayjs()).subtract(1, "day").toDate(),
    ],
    "This Year": [dayjs().startOf("year").toDate(), dayjs().endOf("year").toDate()],
    "Last Year": [
      dayjs().subtract(1, "year").startOf("year").toDate(),
      dayjs().subtract(1, "year").endOf("year").toDate(),
    ],
  },
});

const optionLabel = (options: { value: number | string; label: string }[], value: string) =>
  (value ? options.find((option) => String(option.value) === value)?.label?.trim() : "") ?? "";

const truncateText = (text: string, maxLength = 18) =>
  text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;

export type DashboardFilterSection = "company" | "contact" | "team" | "user" | "status" | "currency" | "date";

type SectionKey = DashboardFilterSection;

interface DashboardFilterDropdownProps {
  title: string;
  filters: DashboardFilterState;
  onChange: (filters: DashboardFilterState) => void;
  loading?: boolean;
  hiddenSections?: DashboardFilterSection[];
  showDefaultDateRange?: boolean;
}

const DashboardFilterDropdown: React.FC<DashboardFilterDropdownProps> = ({
  title,
  filters,
  onChange,
  loading = false,
  hiddenSections = [],
  showDefaultDateRange = false,
}) => {
  const { selectedTenantId } = useContext(AuthContext);
  const isVisible = (key: SectionKey) => !hiddenSections.includes(key);
  const defaults = useDefaultDashboardFilters();
  const [isOpen, setIsOpen] = useState(false);
  const [expanded, setExpanded] = useState<Set<SectionKey>>(() => new Set<SectionKey>());
  const [pickerSettings] = useState(dateRangeSettings);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const { companies, teams, users, isLoading } = useFilterOptions(
    selectedTenantId,
    { companies: isVisible("company"), teams: isVisible("team"), users: isVisible("user") },
    isOpen
  );

  const loadContacts = useCallback(
    (page: number, search: string) =>
      selectedTenantId
        ? fetchContactPage(selectedTenantId, filters.companyId, page, search)
        : Promise.resolve({ options: [], hasMore: false }),
    [selectedTenantId, filters.companyId]
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (target.closest?.(".daterangepicker")) return;
      if (dropdownRef.current && !dropdownRef.current.contains(target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleSection = (key: SectionKey) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const handleCompanyChange = (value: string) => {
    const companyId = value ? Number(value) : null;
    onChange({
      ...filters,
      companyId,
      companyName: optionLabel(companies, value),
      contactId: null,
      contactName: "",
    });
  };

  const handleContactChange = (option: SelectOption | null) => {
    onChange({
      ...filters,
      contactId: option ? Number(option.value) : null,
      contactName: option?.label ?? "",
    });
  };

  const selectedContact: SelectOption | null =
    filters.contactId !== null ? { value: filters.contactId, label: filters.contactName || `Contact #${filters.contactId}` } : null;

  const handleTeamChange = (value: string) => {
    const teamId = value ? Number(value) : null;
    onChange({
      ...filters,
      teamId,
      teamName: optionLabel(teams, value),
    });
  };

  const handleUserChange = (value: string) => {
    const userId = value ? Number(value) : null;
    onChange({
      ...filters,
      userId,
      userName: optionLabel(users, value),
    });
  };

  const handleStatusChange = (value: string) => {
    onChange({ ...filters, status: value || null });
  };

  const handleDateApply = (start: Date, end: Date) => {
    onChange({
      ...filters,
      startDate: dayjs(start).format(API_DATE_FORMAT),
      endDate: dayjs(end).format(API_DATE_FORMAT),
    });
  };

  const handleDateClear = () => {
    onChange({ ...filters, startDate: null, endDate: null });
  };

  const handleCurrencyChange = (value: string) => {
    onChange({ ...filters, currencyCode: value || defaults.currencyCode });
  };

  const activeCount = countActiveFilters(filters, defaults);
  const fallbackRange = showDefaultDateRange ? defaultDateRange() : null;
  const isDefaultDate = filters.startDate === null && fallbackRange !== null;
  const dateLabel = isDefaultDate && fallbackRange
    ? formatDateRange(fallbackRange.startDate, fallbackRange.endDate)
    : formatDateRange(filters.startDate, filters.endDate);
  const dateSectionValue = isDefaultDate ? DEFAULT_DATE_RANGE_LABEL : dateLabel;

  const sectionHeader = (key: SectionKey, sectionTitle: string, value: string) => (
    <button
      type="button"
      className="w-100 btn btn-link text-start text-decoration-none p-2 d-flex justify-content-between align-items-center"
      onClick={() => toggleSection(key)}
      disabled={loading}
    >
      <span className="small text-dark">
        <strong>{sectionTitle}</strong>
      </span>
      <span className="d-flex align-items-center">
        {value && (
          <span className="small text-muted me-2" title={value}>
            {truncateText(value)}
          </span>
        )}
        <i className={`ti ti-chevron-${expanded.has(key) ? "up" : "down"} small text-dark`} />
      </span>
    </button>
  );

  return (
    <div className="position-relative db-filter" ref={dropdownRef}>
      <button
        type="button"
        className="btn btn-outline-light shadow dropdown-toggle position-relative"
        onClick={() => setIsOpen((open) => !open)}
      >
        <img src="/assets/img/icons/filter.svg" alt="Filter" className="me-1 db-filter-icon" />
        <span className="me-2">Filter</span>
        {activeCount > 0 && (
          <span className="position-absolute top-0 start-100 translate-middle p-1 bg-danger border border-light rounded-circle">
            <span className="visually-hidden">Active filters</span>
          </span>
        )}
      </button>

      {isOpen && (
        <div className="dropdown-menu show db-filter-menu">
          <div className="p-3 pb-0">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="mb-0 d-flex align-items-center">
                <img src="/assets/img/icons/filter.svg" alt="Filter" className="me-1 db-filter-icon" />
                {title}
              </h6>
            </div>
          </div>

          <div className="px-3 db-filter-body">
            <div className="border rounded">
              {sectionHeader("company", "Company", filters.companyName)}
              {expanded.has("company") && (
                <div className="p-2 border-top">
                  <select
                    className="form-select form-select-sm"
                    value={filters.companyId ?? ""}
                    onChange={(e) => handleCompanyChange(e.target.value)}
                    disabled={loading || isLoading.companies}
                  >
                    <option value="">All Companies</option>
                    {companies.map((company) => (
                      <option key={company.value} value={company.value}>
                        {company.label}
                      </option>
                    ))}
                  </select>
                  {isLoading.companies && (
                    <div className="text-center mt-2">
                      <div className="spinner-border spinner-border-sm text-primary" role="status" />
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="border rounded mt-2">
              {sectionHeader("contact", "Contact", filters.contactName)}
              {expanded.has("contact") && (
                <div className="p-2 border-top">
                  <PaginatedAsyncSelect
                    key={`${selectedTenantId ?? ""}-${filters.companyId ?? "all"}`}
                    loadOptions={loadContacts}
                    value={selectedContact}
                    onChange={handleContactChange}
                    placeholder={filters.companyId ? "All Contacts in Company" : "All Contacts"}
                    isClearable
                    isDisabled={loading}
                    className="db-filter-contact"
                    noOptionsMessage={() => "No contacts found"}
                  />
                </div>
              )}
            </div>

            <div className="border rounded mt-2">
              {sectionHeader("team", "Team", filters.teamName)}
              {expanded.has("team") && (
                <div className="p-2 border-top">
                  <select
                    className="form-select form-select-sm"
                    value={filters.teamId ?? ""}
                    onChange={(e) => handleTeamChange(e.target.value)}
                    disabled={loading || isLoading.teams}
                  >
                    <option value="">All Teams</option>
                    {teams.map((team) => (
                      <option key={team.value} value={team.value}>
                        {team.label}
                      </option>
                    ))}
                  </select>
                  {isLoading.teams && (
                    <div className="text-center mt-2">
                      <div className="spinner-border spinner-border-sm text-primary" role="status" />
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="border rounded mt-2">
              {sectionHeader("user", "Assigned User", filters.userName)}
              {expanded.has("user") && (
                <div className="p-2 border-top">
                  <select
                    className="form-select form-select-sm"
                    value={filters.userId ?? ""}
                    onChange={(e) => handleUserChange(e.target.value)}
                    disabled={loading || isLoading.users}
                  >
                    <option value="">All Users</option>
                    {users.map((user) => (
                      <option key={user.value} value={user.value}>
                        {user.label}
                      </option>
                    ))}
                  </select>
                  {isLoading.users && (
                    <div className="text-center mt-2">
                      <div className="spinner-border spinner-border-sm text-primary" role="status" />
                    </div>
                  )}
                </div>
              )}
            </div>

            {isVisible("status") && (
              <div className="border rounded mt-2">
                {sectionHeader("status", "Status", statusLabel(filters.status))}
                {expanded.has("status") && (
                  <div className="p-2 border-top">
                    <select
                      className="form-select form-select-sm"
                      value={filters.status ?? ""}
                      onChange={(e) => handleStatusChange(e.target.value)}
                      disabled={loading}
                    >
                      <option value="">All Statuses</option>
                      {DASHBOARD_STATUS_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            )}

            <div className="border rounded mt-2">
              {sectionHeader("currency", "Currency", filters.currencyCode ?? "")}
              {expanded.has("currency") && (
                <div className="p-2 border-top">
                  <CurrencySelect
                    value={filters.currencyCode ?? ""}
                    onChange={handleCurrencyChange}
                    placeholder="Select currency"
                    isClearable={false}
                    isDisabled={loading}
                    className="db-filter-currency"
                  />
                </div>
              )}
            </div>

            <div className="border rounded mt-2">
              {sectionHeader("date", "Date Range", dateSectionValue)}
              {expanded.has("date") && (
                <div className="p-2 border-top">
                  <DateRangePicker
                    initialSettings={pickerSettings}
                    onApply={(_event: any, picker: any) =>
                      handleDateApply(picker.startDate.toDate(), picker.endDate.toDate())
                    }
                    onCancel={handleDateClear}
                  >
                    <input
                      type="text"
                      className="form-control form-control-sm db-filter-date"
                      placeholder="All time"
                      readOnly
                      value={dateLabel}
                      disabled={loading}
                    />
                  </DateRangePicker>
                </div>
              )}
            </div>
          </div>

          <div className="p-3 pt-2 mt-2 border-top">
            <button
              type="button"
              className="btn btn-outline-light btn-sm w-100"
              onClick={() => onChange(defaults)}
              disabled={loading || activeCount === 0}
            >
              <i className="ti ti-refresh me-1" />
              Reset
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardFilterDropdown;
